import React, { useState, useEffect } from 'react';
import { useDataExport } from '../hooks/useDataExport';
import { useGlobalFilters } from '../hooks/useGlobalFilters';
import './ExportModal.css';

/**
 * Универсальный компонент для экспорта данных
 * Поддерживает различные типы данных, форматы и фильтрацию
 */
const ExportModal = ({ isOpen, onClose, dataType, title }) => {
  const { exportData, exporting, exportProgress, cancelExport } = useDataExport();
  const globalFilters = useGlobalFilters();
  
  // Локальные фильтры (с возможностью изменения)
  const [filters, setFilters] = useState({
    selectedGroups: [],
    startDate: '',
    endDate: ''
  });
  
  const [format, setFormat] = useState('csv');
  const [error, setError] = useState(null);

  // Инициализация фильтров из глобальных настроек
  useEffect(() => {
    if (isOpen && globalFilters) {
      setFilters({
        selectedGroups: globalFilters.selectedGroups || [],
        startDate: globalFilters.startDate || '',
        endDate: globalFilters.endDate || ''
      });
      setError(null);
    }
  }, [isOpen, globalFilters]);

  // Обработчик экспорта
  const handleExport = async () => {
    try {
      setError(null);
      
      // Валидация
      if (!filters.selectedGroups || filters.selectedGroups.length === 0) {
        setError('Выберите хотя бы одну группу');
        return;
      }
      
      await exportData(dataType, filters, format);
      
      // Закрываем модальное окно после успешного экспорта
      setTimeout(() => {
        if (!exporting) {
          onClose();
        }
      }, 500);
      
    } catch (err) {
      console.error('Ошибка экспорта:', err);
      setError(err.message || 'Произошла ошибка при экспорте');
    }
  };

  // Обработчик отмены экспорта
  const handleCancel = () => {
    if (exporting) {
      cancelExport();
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="export-modal-overlay" onClick={handleCancel}>
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h2>{title || 'Экспорт данных'}</h2>
          <button
            className="export-modal-close"
            onClick={handleCancel}
            disabled={exporting}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="export-modal-content">
          {/* Настройки фильтров */}
          <div className="export-section">
            <h3>Фильтры</h3>
            
            <div className="export-filter-info">
              <div className="filter-item">
                <label>Выбрано групп:</label>
                <span className="filter-value">
                  {filters.selectedGroups?.length || 0}
                </span>
              </div>
              
              <div className="filter-item">
                <label>Период:</label>
                <div className="date-range">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    disabled={exporting}
                    className="date-input"
                  />
                  <span className="date-separator">—</span>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    disabled={exporting}
                    className="date-input"
                  />
                </div>
              </div>
            </div>
            
            <p className="filter-note">
              💡 Фильтры взяты из глобального фильтра. Вы можете изменить даты здесь.
            </p>
          </div>

          {/* Выбор формата */}
          <div className="export-section">
            <h3>Формат файла</h3>
            <div className="format-options">
              <label className={`format-option ${format === 'csv' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={(e) => setFormat(e.target.value)}
                  disabled={exporting}
                />
                <div className="format-card">
                  <span className="format-icon">📄</span>
                  <span className="format-name">CSV</span>
                  <span className="format-desc">Текстовый формат, совместим со всеми программами</span>
                </div>
              </label>
              
              <label className={`format-option ${format === 'xlsx' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="xlsx"
                  checked={format === 'xlsx'}
                  onChange={(e) => setFormat(e.target.value)}
                  disabled={exporting}
                />
                <div className="format-card">
                  <span className="format-icon">📊</span>
                  <span className="format-name">XLSX</span>
                  <span className="format-desc">Excel файл, лучше для анализа в таблицах</span>
                </div>
              </label>
            </div>
          </div>

          {/* Прогресс экспорта */}
          {exporting && (
            <div className="export-section">
              <h3>Прогресс экспорта</h3>
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${exportProgress.percent}%` }}
                  />
                </div>
                <div className="progress-text">
                  {exportProgress.current} / {exportProgress.total} записей ({exportProgress.percent}%)
                </div>
              </div>
            </div>
          )}

          {/* Сообщение об ошибке */}
          {error && (
            <div className="export-error">
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="export-modal-footer">
          <button
            className="btn-secondary"
            onClick={handleCancel}
            disabled={exporting}
          >
            {exporting ? 'Отменить' : 'Закрыть'}
          </button>
          <button
            className="btn-primary"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Экспортируем...' : 'Экспортировать'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;

